import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from '@components/atoms/Info.tsx';
import { classNames } from '@utils/classNames.ts';

interface AsbestosBannerComponentProps {
  header: string;
  onInfoClick: () => void;
  type: 'success' | 'failure';
  samplePreparation: string;
  procedure: string;
  proofThreshold: string;
  finding: string;
}

export const AsbestosBannerComponent: React.FC<
  AsbestosBannerComponentProps
> = ({
  header,
  onInfoClick,
  type,
  samplePreparation,
  procedure,
  proofThreshold,
  finding,
}) => {
  const { t } = useTranslation('component', {
    keyPrefix: 'parameterList.asbestosDetection',
  });

  // Determine background color based on ParameterStatusType
  const getBackgroundColor = (type: 'success' | 'failure'): string => {
    switch (type) {
      case 'failure':
        return 'bg-error';
      case 'success':
        return 'bg-green';
    }
  };

  const backgroundColor = getBackgroundColor(type);
  const textColor = 'text-white';

  return (
    <div className="w-full font-sans">
      <div className={classNames('p-6 rounded-lg', backgroundColor, textColor)}>
        <div className="space-y-4">
          {/* Header with info icon */}
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-lg">{header}</h3>
            <Info className="bg-white w-3.5 h-3.5" onClick={onInfoClick} />
          </div>

          {/* Data points */}
          <div className="space-y-3">
            {/* Sample Preparation */}
            {samplePreparation && (
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="font-thin sm:no-underline underline w-full sm:w-[30%]">
                  {t('samplePreparation')}:
                </div>
                <div className="font-thin w-full sm:w-[70%]">
                  {samplePreparation}
                </div>
              </div>
            )}

            {/* Procedure */}
            {procedure && (
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="font-thin sm:no-underline underline w-full sm:w-[30%]">
                  {t('procedure')}:
                </div>
                <div className="font-thin w-full sm:w-[70%]">{procedure}</div>
              </div>
            )}

            {/* Proof Threshold */}
            {proofThreshold && (
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="font-thin sm:no-underline underline w-full sm:w-[30%]">
                  {t('proofThreshold')}:
                </div>
                <div className="font-thin w-full sm:w-[70%]">
                  {proofThreshold}
                </div>
              </div>
            )}

            {/* Finding */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="font-thin sm:no-underline underline w-full sm:w-[30%]">
                {t('finding')}:
              </div>
              <div className="font-semibold w-full sm:w-[70%]">{finding}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AsbestosBannerComponent;
